import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Typography, Select, Spin, Tag, Tooltip, Modal, Image, Space, Popconfirm } from "antd";
import {
  CheckCircleFilled,
  ExclamationCircleFilled,
  LoadingOutlined,
  DeleteOutlined,
  EyeOutlined
} from "@ant-design/icons";
import styled from "styled-components";

import { useApi, ApiNames } from "../../hooks/api";
import {
  WHITE,
  GRAY_25,
  GRAY_200,
  GRAY_300,
  GRAY_500,
  GRAY_700,
  PRIMARY_600,
  SUCCESS_600,
  WARNING_50,
  WARNING_200,
  WARNING_700
} from "../../constant/colors";
import { ActionButton, SecondaryActionButton } from "./taskStyles";
import { patientLabel } from "./taskHelpers";
import {
  EMR_SECTIONS,
  groupReview,
  issueKey,
  missingSectionLabelKeys,
  sectionLabelKey,
  suggestedTaskId,
  toImageDataUri
} from "./screenshotReview";

const { Text } = Typography;

const Panel = styled.div`
  margin: 0 16px 12px;
  border: 1px solid ${GRAY_200};
  border-radius: 8px;
  background: ${WHITE};
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  border-bottom: 1px solid ${GRAY_200};
`;

const Group = styled.div`
  padding: 12px 14px;
  border-bottom: 1px solid ${GRAY_200};
  &:last-child {
    border-bottom: 0;
  }
  ${(p) => (p.$attention ? `background: ${WARNING_50};` : "")}
`;

const GroupHeader = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 10px;
`;

const Cards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 10px;
`;

const Card = styled.div`
  position: relative;
  border: 1px solid ${(p) => (p.$attention ? WARNING_200 : GRAY_300)};
  border-radius: 8px;
  padding: 8px;
  background: ${WHITE};
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Thumb = styled.img`
  width: 100%;
  height: 90px;
  object-fit: contain;
  background: ${GRAY_25};
  border-radius: 4px;
`;

const ThumbPlaceholder = styled.button`
  width: 100%;
  height: 90px;
  border: 1px dashed ${GRAY_300};
  border-radius: 4px;
  background: ${GRAY_25};
  color: ${GRAY_500};
  cursor: pointer;
  font-size: 12px;
`;

const IconButton = styled.button`
  position: absolute;
  top: 4px;
  right: 4px;
  background: ${WHITE};
  border: none;
  border-radius: 50%;
  padding: 2px;
  line-height: 0;
  cursor: pointer;
  color: ${GRAY_500};
  &:hover {
    color: ${PRIMARY_600};
  }
`;

const Small = styled(Text)`
  font-size: 12px;
  color: ${GRAY_700};
`;

/**
 * "Medications ✓ · Coverages —" chips for one screenshot or one patient.
 * `sections` are the site's required sections (see `emrSectionsFor`).
 */
const SectionChips = ({ shownSections, testId, sections = EMR_SECTIONS }) => {
  const { t } = useTranslation();
  return (
    <Space size={4} wrap data-testid={testId}>
      {sections.map((section) => {
        const shown = Boolean(shownSections?.[section]);
        return (
          <Tag key={section} color={shown ? "success" : "default"} style={{ marginInlineEnd: 0 }}>
            {shown ? "✓ " : ""}
            {t(sectionLabelKey(section))}
          </Tag>
        );
      })}
    </Space>
  );
};

/**
 * Thumbnail for one screenshot. Uses the operator's own local file when this
 * session uploaded it; otherwise (e.g. after a reload) loads the image on
 * demand through FN's PHI-gated endpoint.
 */
const ScreenshotThumb = ({ view, previewUrl }) => {
  const { t } = useTranslation();
  const [fetchImage, image, loading] = useApi(ApiNames.AgenticEmrScreenshotImage);
  const [open, setOpen] = useState(false);
  const src = previewUrl || toImageDataUri(image);
  const alt = t("agentic_platform.emr_tasks.screenshot_alt", { id: view.screenshotId });

  const openPreview = () => {
    if (!src) fetchImage(view.screenshotId);
    setOpen(true);
  };

  return (
    <>
      {src ? (
        <Thumb
          src={src}
          alt={alt}
          onClick={openPreview}
          style={{ cursor: "zoom-in" }}
          data-testid={`agentic-review-view-${view.screenshotId}`}
        />
      ) : (
        <ThumbPlaceholder
          type="button"
          onClick={openPreview}
          aria-label={t("agentic_platform.emr_tasks.view_screenshot")}
          data-testid={`agentic-review-view-${view.screenshotId}`}
        >
          {loading ? <Spin size="small" /> : <EyeOutlined />} {t("agentic_platform.emr_tasks.view_screenshot")}
        </ThumbPlaceholder>
      )}
      <Modal open={open} onCancel={() => setOpen(false)} footer={null} width={900} title={alt}>
        {src ? (
          <Image src={src} alt={alt} preview={false} style={{ maxWidth: "100%" }} />
        ) : (
          <div style={{ display: "flex", justifyContent: "center", padding: 32 }}>
            <Spin />
          </div>
        )}
      </Modal>
    </>
  );
};

const ScreenshotCard = ({ view, previewUrl, attention, busy, onDiscard, children }) => {
  const { t } = useTranslation();
  return (
    <Card $attention={attention} data-testid={`agentic-review-screenshot-${view.screenshotId}`}>
      {/* Compact, anchored below the corner icon so it doesn't cover the
          group header; the app zeroes popover horizontal padding
          (@popover-padding-horizontal in craco.config.js), so restore it
          here like Uploader's delete confirm does. */}
      <Popconfirm
        title={t("agentic_platform.emr_tasks.discard_confirm")}
        okText={t("agentic_platform.emr_tasks.discard")}
        cancelText={t("agentic_platform.emr_tasks.submit_cancel")}
        onConfirm={() => onDiscard(view.screenshotId)}
        disabled={busy}
        placement="bottomRight"
        arrowPointAtCenter
        overlayInnerStyle={{ padding: "0px 12px" }}
        okButtonProps={{ danger: true, size: "small" }}
        cancelButtonProps={{ size: "small" }}
      >
        <IconButton
          type="button"
          aria-label={t("agentic_platform.emr_tasks.discard")}
          title={t("agentic_platform.emr_tasks.discard")}
          data-testid={`agentic-review-discard-${view.screenshotId}`}
        >
          <DeleteOutlined />
        </IconButton>
      </Popconfirm>
      <ScreenshotThumb view={view} previewUrl={previewUrl} />
      {children}
    </Card>
  );
};

/**
 * The upload review: what the agent made of each screenshot the operator just
 * dropped, grouped by the patient it identified. Everything the operator must
 * act on is here — screenshots it couldn't attribute (with the reason and an
 * "assign to" picker), patients still missing a section, and the Submit per
 * patient. Nothing is sent to the agent until Submit. `sections` are the
 * site's required EMR sections (see `emrSectionsFor`).
 */
const ScreenshotReviewPanel = ({
  views,
  previews,
  tasksById,
  openTasks,
  tasksTruncated,
  busy,
  onAssign,
  onDiscard,
  onSubmit,
  onClear,
  sections = EMR_SECTIONS
}) => {
  const { t } = useTranslation();
  // The "assign to" pickers are uncontrolled (a successful assign moves the
  // screenshot out of the tray); bumping this remounts them after a failed
  // one so they don't keep showing a patient the screenshot isn't assigned to.
  const [assignResetNonce, setAssignResetNonce] = useState(0);
  const { groups, attention, identifying } = groupReview(views, tasksById, sections);
  if (groups.length + attention.length + identifying.length === 0) return null;

  const assignFromTray = async (screenshotId, taskId) => {
    const assigned = await onAssign(screenshotId, taskId);
    if (!assigned) setAssignResetNonce((nonce) => nonce + 1);
  };

  const suggestion = suggestedTaskId(groups);
  const submittableTaskIds = groups.map((group) => group.taskId);
  const taskOptions = (openTasks || []).map((task) => ({
    value: task.taskId,
    label:
      task.taskId === suggestion
        ? t("agentic_platform.emr_tasks.assign_suggested", { patient: patientLabel(task) })
        : patientLabel(task)
  }));
  // The suggested patient leads the picker.
  taskOptions.sort((a, b) => (a.value === suggestion ? -1 : b.value === suggestion ? 1 : 0));
  // A group's task can leave the open list (e.g. once it's submitted) while its
  // screenshots are still in the review — keep it pickable so the Select shows
  // the patient rather than the bare task id.
  const optionsForGroup = (group) =>
    taskOptions.some((option) => option.value === group.taskId)
      ? taskOptions
      : [{ value: group.taskId, label: patientLabel(group) }, ...taskOptions];

  return (
    <Panel data-testid="agentic-screenshot-review">
      <PanelHeader>
        <Text strong>{t("agentic_platform.emr_tasks.review_title")}</Text>
        {identifying.length > 0 && (
          <Small data-testid="agentic-review-identifying">
            <LoadingOutlined style={{ color: PRIMARY_600, marginRight: 6 }} />
            {t("agentic_platform.emr_tasks.review_identifying", { count: identifying.length })}
          </Small>
        )}
        <Space style={{ marginLeft: "auto" }}>
          {onClear && (
            <SecondaryActionButton onClick={onClear} disabled={busy} data-testid="agentic-review-clear">
              {t("agentic_platform.emr_tasks.review_clear")}
            </SecondaryActionButton>
          )}
          {submittableTaskIds.length > 1 && (
            <ActionButton
              onClick={() => onSubmit(submittableTaskIds)}
              loading={busy}
              data-testid="agentic-review-submit-all"
            >
              {t("agentic_platform.emr_tasks.review_submit_all", { count: submittableTaskIds.length })}
            </ActionButton>
          )}
        </Space>
      </PanelHeader>

      {tasksTruncated && (
        <Group $attention data-testid="agentic-review-tasks-truncated">
          <Small style={{ color: WARNING_700 }}>
            <ExclamationCircleFilled style={{ color: WARNING_700, marginRight: 6 }} />
            {t("agentic_platform.emr_tasks.review_tasks_truncated", { count: (openTasks || []).length })}
          </Small>
        </Group>
      )}

      {attention.length > 0 && (
        <Group $attention data-testid="agentic-review-attention">
          <GroupHeader>
            <ExclamationCircleFilled style={{ color: WARNING_700 }} />
            <Text strong style={{ color: WARNING_700 }}>
              {t("agentic_platform.emr_tasks.review_attention_title", { count: attention.length })}
            </Text>
          </GroupHeader>
          <Cards>
            {attention.map((view) => (
              <ScreenshotCard
                key={view.screenshotId}
                view={view}
                previewUrl={previews.get(view.screenshotId)}
                attention
                busy={busy}
                onDiscard={onDiscard}
              >
                <Small data-testid={`agentic-review-issue-${view.screenshotId}`}>
                  {t(`agentic_platform.emr_tasks.issue.${issueKey(view)}`)}
                </Small>
                <Select
                  key={assignResetNonce}
                  size="small"
                  placeholder={t("agentic_platform.emr_tasks.assign_placeholder")}
                  options={taskOptions}
                  disabled={busy || taskOptions.length === 0}
                  onChange={(taskId) => assignFromTray(view.screenshotId, taskId)}
                  showSearch
                  optionFilterProp="label"
                  aria-label={t("agentic_platform.emr_tasks.assign_placeholder")}
                  data-testid={`agentic-review-assign-${view.screenshotId}`}
                  style={{ width: "100%" }}
                />
              </ScreenshotCard>
            ))}
          </Cards>
        </Group>
      )}

      {groups.map((group) => (
        <Group key={group.taskId} data-testid={`agentic-review-group-${group.taskId}`}>
          <GroupHeader>
            {group.missing.length === 0 ? (
              <CheckCircleFilled style={{ color: SUCCESS_600 }} />
            ) : (
              <ExclamationCircleFilled style={{ color: WARNING_700 }} />
            )}
            <Text strong>{patientLabel(group)}</Text>
            <SectionChips
              shownSections={group.shownSections}
              sections={sections}
              testId={`agentic-review-sections-${group.taskId}`}
            />
            {group.missing.length > 0 && (
              <Small style={{ color: WARNING_700 }} data-testid={`agentic-review-missing-${group.taskId}`}>
                {t("agentic_platform.emr_tasks.review_missing", {
                  sections: missingSectionLabelKeys(group.missing)
                    .map((key) => t(key))
                    .join(", ")
                })}
              </Small>
            )}
            <Tooltip
              title={group.missing.length > 0 ? t("agentic_platform.emr_tasks.review_submit_partial_hint") : undefined}
            >
              <ActionButton
                style={{ marginLeft: "auto" }}
                onClick={() => onSubmit([group.taskId])}
                loading={busy}
                data-testid={`agentic-review-submit-${group.taskId}`}
              >
                {t("agentic_platform.emr_tasks.review_submit_patient")}
              </ActionButton>
            </Tooltip>
          </GroupHeader>
          <Cards>
            {group.screenshots.map((view) => (
              <ScreenshotCard
                key={view.screenshotId}
                view={view}
                previewUrl={previews.get(view.screenshotId)}
                busy={busy}
                onDiscard={onDiscard}
              >
                <SectionChips shownSections={view.shownSections} sections={sections} />
                {view.assignedBy === "operator" && <Small>{t("agentic_platform.emr_tasks.assigned_by_you")}</Small>}
                <Select
                  size="small"
                  value={group.taskId}
                  options={optionsForGroup(group)}
                  disabled={busy}
                  onChange={(taskId) => taskId !== group.taskId && onAssign(view.screenshotId, taskId)}
                  showSearch
                  optionFilterProp="label"
                  aria-label={t("agentic_platform.emr_tasks.reassign")}
                  data-testid={`agentic-review-reassign-${view.screenshotId}`}
                  style={{ width: "100%" }}
                />
              </ScreenshotCard>
            ))}
          </Cards>
        </Group>
      ))}

      {identifying.length > 0 && (
        <Group data-testid="agentic-review-identifying-group">
          <Cards>
            {identifying.map((view) => (
              <ScreenshotCard
                key={view.screenshotId}
                view={view}
                previewUrl={previews.get(view.screenshotId)}
                busy={busy}
                onDiscard={onDiscard}
              >
                <Small>
                  <LoadingOutlined style={{ marginRight: 6 }} />
                  {t("agentic_platform.emr_tasks.identifying_one")}
                </Small>
              </ScreenshotCard>
            ))}
          </Cards>
        </Group>
      )}
    </Panel>
  );
};

export { ScreenshotReviewPanel, SectionChips };
